import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { WpDialog, CheckInput } from './DialogShell';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FontPicker } from '../editor/FontPicker';
import { ArrowRight, Loader2, Upload, AlertTriangle, CheckCircle2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { collectDocumentFonts, fontStatuses, uploadUserFont, onFontsChanged } from '../../lib/fontLoader';
import { swapFonts, fontUsage, suggestReplacement } from '../../lib/fontSwap';
import { fontPairs } from '../../lib/fontPairs';
import { apiErrorMessage } from '../../lib/projectApi';

/**
 * Missing Font Swap: every family the publication uses that is not available here (not installed, not on Google Fonts, not
 * uploaded) is listed with a suggested replacement; "Replace Fonts" rewrites all of them across pages, masters, tables, rich
 * text runs and inline HTML in one undoable step. The real font file can be uploaded instead (My fonts), which clears the row.
 */
export const FontSubstitutionDialog = ({ props }) => {
  const { state, dispatch, closeDialog } = useEditor();
  const docFonts = useMemo(() => collectDocumentFonts(state.pages, state.masters), [state.pages, state.masters]);
  const usage = useMemo(() => fontUsage(state.pages, state.masters), [state.pages, state.masters]);
  const [statuses, setStatuses] = useState(null); // [{ family, status }]
  const [rows, setRows] = useState(() => (props.fonts || []).map((f) => ({ from: f, to: suggestReplacement(f), on: true })));
  const [uploading, setUploading] = useState(false);
  const [extra, setExtra] = useState('');
  const fileRef = useRef(null);

  // Availability of every family in the document (re-checked when fonts are uploaded)
  useEffect(() => {
    let cancelled = false;
    const check = () => fontStatuses(docFonts).then((st) => { if (!cancelled) setStatuses(st); });
    check();
    const off = onFontsChanged(check);
    return () => { cancelled = true; off(); };
  }, [docFonts]);

  // Add every missing family as a row (keeps rows the user already edited)
  useEffect(() => {
    if (!statuses) return;
    const missing = statuses.filter((s) => s.status === 'missing').map((s) => s.family);
    setRows((prev) => {
      const have = new Set(prev.map((r) => r.from.toLowerCase()));
      const add = missing.filter((f) => !have.has(f.toLowerCase())).map((f) => ({ from: f, to: suggestReplacement(f), on: true }));
      return add.length ? [...prev, ...add] : prev;
    });
  }, [statuses]);

  const statusOf = (family) => statuses?.find((s) => s.family.toLowerCase() === family.toLowerCase())?.status;
  const setRow = (i, patch) => setRows((prev) => prev.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const active = rows.filter((r) => r.on && r.to && r.to.toLowerCase() !== r.from.toLowerCase());
  const others = docFonts.filter((f) => !rows.some((r) => r.from.toLowerCase() === f.toLowerCase()));

  const apply = () => {
    const mapping = Object.fromEntries(active.map((r) => [r.from, r.to]));
    const { pages, masters, count } = swapFonts(state.pages, state.masters, mapping);
    if (!count) { toast.info('Nothing to replace — none of these fonts are used any more.'); closeDialog(); return; }
    dispatch({ type: 'REPLACE_DOCUMENT_CONTENT', payload: { pages, masters } });
    toast.success(`${active.length === 1 ? `${active[0].from} → ${active[0].to}` : `${active.length} fonts replaced`} · ${count} place${count === 1 ? '' : 's'} updated`, { description: 'Ctrl+Z undoes the substitution.' });
    closeDialog();
  };

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    let added = 0;
    for (const f of list) { try { await uploadUserFont(f); added += 1; } catch (e) { toast.error(`${f.name}: ${await apiErrorMessage(e, 'could not be uploaded.')}`); } }
    setUploading(false);
    if (added) toast.success(`${added === 1 ? 'Font added' : `${added} fonts added`} — the publication uses ${added === 1 ? 'it' : 'them'} right away.`);
  };

  return (
    <WpDialog
      name="font-substitution"
      title="Replace Fonts"
      description="Fonts that are not available here are shown in a substitute face. Pick the font to use instead — it is applied everywhere in the publication — or upload the real font file."
      width="w-[640px]"
      onCancel={closeDialog}
      onOk={apply}
      okLabel={active.length ? `Replace ${active.length === 1 ? 'Font' : `${active.length} Fonts`}` : 'Replace Fonts'}
      okDisabled={!active.length}
      extraFooter={(
        <>
          <input ref={fileRef} type="file" accept=".ttf,.otf,.woff,.woff2" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ''; }} data-testid="font-sub-upload-input" />
          <Button variant="ghost" disabled={uploading} onClick={() => fileRef.current?.click()} className="mr-auto" data-testid="font-sub-upload-button">{uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Upload the real font instead…</Button>
        </>
      )}
    >
      <div className="space-y-3" data-testid="font-substitution">
        {!statuses && <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="font-sub-checking"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking which fonts are available…</div>}
        {statuses && rows.length === 0 && (
          <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm text-emerald-800 dark:text-emerald-200" data-testid="font-sub-none"><CheckCircle2 className="h-4 w-4" /> Every font in this publication is available — nothing needs replacing.</div>
        )}
        {rows.length > 0 && (
          <div className="rounded-md border border-border divide-y divide-border" data-testid="font-sub-rows">
            {rows.map((r, i) => {
              const st = statusOf(r.from);
              const available = st && st !== 'missing';
              const n = usage.get(r.from) || 0;
              return (
                <div key={r.from} className="grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto_1fr] items-center gap-x-3 gap-y-1.5 px-3 py-2" data-testid={`font-sub-row-${r.from.replace(/\s+/g, '-')}`}>
                  <CheckInput checked={r.on} onChange={(v) => setRow(i, { on: v })} label="" testId={`font-sub-on-${r.from.replace(/\s+/g, '-')}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" title={r.from}>{r.from}</div>
                    <div className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-1">
                      {available ? <><CheckCircle2 className="h-3 w-3 text-emerald-600" /> now available{st === 'user' ? ' (My fonts)' : ''}</> : <><AlertTriangle className="h-3 w-3 text-amber-500" /> not available</>}
                      {n > 0 && <span>· used in {n} place{n === 1 ? '' : 's'}</span>}
                    </div>
                  </div>
                  <ArrowRight className="hidden sm:block h-4 w-4 text-muted-foreground" />
                  <div className={`col-start-2 sm:col-start-auto ${r.on ? '' : 'opacity-50 pointer-events-none'}`}>
                    <FontPicker value={r.to} onChange={(f) => setRow(i, { to: f })} testId={`font-sub-picker-${r.from.replace(/\s+/g, '-')}`} allowSaveScheme={false} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {others.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-muted-foreground">Replace another font used in this publication:</span>
            <Select value={extra} onValueChange={setExtra}>
              <SelectTrigger className="h-7 w-[200px] text-xs bg-background border-border" data-testid="font-sub-extra-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>
                {others.map((f) => <SelectItem key={f} value={f} className="text-xs" data-testid={`font-sub-extra-option-${f.replace(/\s+/g, '-')}`}><span style={{ fontFamily: `"${f}"` }}>{f}</span>{usage.get(f) ? <span className="text-muted-foreground"> ({usage.get(f)})</span> : null}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!extra} onClick={() => { setRows((prev) => [...prev, { from: extra, to: statusOf(extra) && statusOf(extra) !== 'missing' ? (fontPairs(extra, 1)[0] || suggestReplacement(extra)) : suggestReplacement(extra), on: true }]); setExtra(''); }} data-testid="font-sub-extra-add"><Plus className="h-3 w-3 mr-1" /> Add</Button>
          </div>
        )}
      </div>
    </WpDialog>
  );
};
